import { soundKeys } from "../audio/sounds";
import type { OverlayHandle, QueueOverlayHandle } from "../app_overlays";
import type { AudioManager } from "../audio/audio";
import type { I18n } from "../i18n/i18n";
import { InputManager } from "../input/input";
import type { OnlineClient } from "../net/net";
import type { GameSnapshot, RenderAudioEvents, Renderer } from "../render/renderer";
import type { SavedProfile } from "../storage/storage";
import type { GameLoop } from "./loop";
import type { OfflineGame } from "./offline";
import type { SnapshotInterpolator } from "./snapshot_interpolator";

const DAMAGE_SOUND_COOLDOWN_MS = 85;
const SHOT_SOUND_COOLDOWN_MS = 70;
const HEART_PICKUP_SOUND_COOLDOWN_MS = 90;
const LEVEL_UP_SOUND_COOLDOWN_MS = 250;
export const DEFAULT_END_SCREEN_TIMEOUT_MS = 30000;

export type GameSession = {
  stop: () => void;
};

export type SessionOptions = {
  root: HTMLElement;
  profile: SavedProfile;
  i18n: I18n;
  audio: AudioManager;
  onReturnToMenu: () => void;
};

export type SessionMode = "offline" | "online";

export type GameEndResult = {
  winnerPlayerId: unknown;
  won: boolean;
  timeoutMs: number;
};

export type DefeatResult = {
  timeoutMs: number;
};

export type ServerErrorDetails = {
  code: string;
  message: string;
};

export type SessionState = {
  loop: GameLoop | null;
  input: InputManager | null;
  renderer: Renderer | null;
  offlineGame: OfflineGame | null;
  onlineClient: OnlineClient | null;
  currentSnapshot: GameSnapshot | null;
  renderSnapshot: GameSnapshot | null;
  snapshotInterpolator: SnapshotInterpolator | null;
  upgradeOverlay: OverlayHandle | null;
  defeatOverlay: OverlayHandle | null;
  gameEndOverlay: OverlayHandle | null;
  pauseOverlay: OverlayHandle | null;
  queueOverlay: QueueOverlayHandle | null;
  gameEndResult: GameEndResult | null;
  defeatTimeoutMs: number | null;
  hasMoved: boolean;
  upgradeSignature: string;
  defeatSoundPlayed: boolean;
  respawning: boolean;
  menuOpen: boolean;
};

export function createState(): SessionState {
  return {
    loop: null,
    input: null,
    renderer: null,
    offlineGame: null,
    onlineClient: null,
    currentSnapshot: null,
    renderSnapshot: null,
    snapshotInterpolator: null,
    upgradeOverlay: null,
    defeatOverlay: null,
    gameEndOverlay: null,
    pauseOverlay: null,
    queueOverlay: null,
    gameEndResult: null,
    defeatTimeoutMs: null,
    hasMoved: false,
    upgradeSignature: "",
    defeatSoundPlayed: false,
    respawning: false,
    menuOpen: false
  };
}

export function createInput(
  canvas: HTMLCanvasElement,
  renderer: Renderer,
  state: SessionState
): InputManager {
  return new InputManager({
    target: canvas,
    getPlayerScreenPosition: () =>
      renderer.getPlayerScreenPosition(state.renderSnapshot ?? state.currentSnapshot),
    onChange: (movement) => {
      if (
        movement.active &&
        !state.menuOpen &&
        !state.queueOverlay &&
        !state.currentSnapshot?.defeated &&
        !state.respawning &&
        !state.gameEndResult
      ) {
        state.hasMoved = true;
      }
    }
  });
}

export class GameplayAudio {
  private lastDamageAt = 0;
  private lastShotAt = 0;
  private lastHeartPickupAt = 0;
  private lastLevelUpAt = 0;

  public constructor(private readonly options: SessionOptions) {}

  public play(events: RenderAudioEvents): void {
    const now = performance.now();

    if (events.visibleDamage && now - this.lastDamageAt >= DAMAGE_SOUND_COOLDOWN_MS) {
      this.lastDamageAt = now;
      this.options.audio.play(soundKeys.damage);
    }

    if (events.localShot && now - this.lastShotAt >= SHOT_SOUND_COOLDOWN_MS) {
      this.lastShotAt = now;
      this.options.audio.play(soundKeys.shot);
    }

    if (events.localHeartPickup && now - this.lastHeartPickupAt >= HEART_PICKUP_SOUND_COOLDOWN_MS) {
      this.lastHeartPickupAt = now;
      this.options.audio.play(soundKeys.heartPickup);
    }

    if (events.localLevelUp && now - this.lastLevelUpAt >= LEVEL_UP_SOUND_COOLDOWN_MS) {
      this.lastLevelUpAt = now;
      this.options.audio.play(soundKeys.levelUp);
    }
  }
}

export function createGameplayUi(options: SessionOptions, state: SessionState) {
  return {
    instructionVisible:
      !state.menuOpen &&
      !state.queueOverlay &&
      !state.hasMoved &&
      !state.currentSnapshot?.defeated &&
      !state.respawning &&
      !state.gameEndResult,
    instructionText: options.i18n.t(isTouchDevice() ? "game.touchToMove" : "game.pressToMove"),
    statusText: state.respawning ? options.i18n.t("game.respawning") : "",
    labels: {
      leaderboard: options.i18n.t("game.leaderboard"),
      hp: options.i18n.t("game.hp"),
      level: options.i18n.t("game.level"),
      xp: options.i18n.t("game.xp"),
      defeatTitle: options.i18n.t("defeat.title"),
      defeatSubtitle: options.i18n.t("defeat.subtitle")
    }
  };
}

export function cleanup(state: SessionState): void {
  state.loop?.stop();
  state.input?.detach();
  state.onlineClient?.disconnect();

  state.upgradeOverlay?.destroy();
  state.defeatOverlay?.destroy();
  state.gameEndOverlay?.destroy();
  state.pauseOverlay?.destroy();
  state.queueOverlay?.destroy();
  state.snapshotInterpolator?.clear();

  state.loop = null;
  state.input = null;
  state.renderer = null;
  state.offlineGame = null;
  state.onlineClient = null;
  state.currentSnapshot = null;
  state.renderSnapshot = null;
  state.snapshotInterpolator = null;
  state.upgradeOverlay = null;
  state.defeatOverlay = null;
  state.gameEndOverlay = null;
  state.pauseOverlay = null;
  state.queueOverlay = null;
  state.gameEndResult = null;
  state.defeatTimeoutMs = null;
  state.menuOpen = false;
}

export function extractSnapshot(message: unknown): GameSnapshot | null {
  if (!message || typeof message !== "object") {
    return null;
  }

  const record = message as Record<string, unknown>;

  if ("Snapshot" in record) {
    return record.Snapshot as GameSnapshot;
  }

  if ("snapshot" in record) {
    return record.snapshot as GameSnapshot;
  }

  if ("Joined" in record) {
    const joined = record.Joined as { snapshot?: GameSnapshot };
    return joined.snapshot ?? null;
  }

  if ("joined" in record) {
    const joined = record.joined as { snapshot?: GameSnapshot };
    return joined.snapshot ?? null;
  }

  return null;
}

export function extractDefeat(message: unknown): DefeatResult | null {
  if (message === "Defeat" || message === "defeat") {
    return {
      timeoutMs: DEFAULT_END_SCREEN_TIMEOUT_MS
    };
  }

  if (!message || typeof message !== "object") {
    return null;
  }

  const record = message as Record<string, unknown>;
  const payload = "Defeat" in record ? record.Defeat : "defeat" in record ? record.defeat : null;

  if (payload === true) {
    return {
      timeoutMs: DEFAULT_END_SCREEN_TIMEOUT_MS
    };
  }

  if (!payload || typeof payload !== "object") {
    return null;
  }

  const defeat = payload as Record<string, unknown>;

  return {
    timeoutMs: readTimeoutMs(defeat)
  };
}

export function isDefeatMessage(message: unknown): boolean {
  return extractDefeat(message) !== null;
}
export function extractServerErrorDetails(message: unknown): ServerErrorDetails | null {
  if (!message || typeof message !== "object") {
    return null;
  }

  const record = message as Record<string, unknown>;
  const payload = "Error" in record ? record.Error : "error" in record ? record.error : null;

  if (!payload || typeof payload !== "object") {
    return null;
  }

  const error = payload as Record<string, unknown>;
  const code = typeof error.code === "string" ? error.code : "server_error";
  const messageText = typeof error.message === "string" ? error.message : code;

  return {
    code,
    message: messageText
  };
}

export function extractServerError(message: unknown): string | null {
  return extractServerErrorDetails(message)?.message ?? null;
}

export function extractGameEnd(message: unknown): GameEndResult | null {
  if (!message || typeof message !== "object") {
    return null;
  }

  const record = message as Record<string, unknown>;
  const payload = "GameEnd" in record ? record.GameEnd : "gameEnd" in record ? record.gameEnd : null;

  if (!payload || typeof payload !== "object") {
    return null;
  }

  const gameEnd = payload as Record<string, unknown>;

  return {
    winnerPlayerId: gameEnd.winner_player_id ?? gameEnd.winnerPlayerId ?? null,
    won: gameEnd.won === true,
    timeoutMs: readTimeoutMs(gameEnd)
  };
}

function readTimeoutMs(record: Record<string, unknown>): number {
  const timeoutMs = record.timeout_ms ?? record.timeoutMs;

  if (typeof timeoutMs === "number" && Number.isFinite(timeoutMs) && timeoutMs > 0) {
    return timeoutMs;
  }

  return DEFAULT_END_SCREEN_TIMEOUT_MS;
}

function isTouchDevice(): boolean {
  return matchMedia("(pointer: coarse)").matches;
}